import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import api from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import LoadingState from '../../components/common/LoadingState';
import { useConfirm } from '../../components/confirm/ConfirmDialogProvider';
import { notifyError, notifyWarning } from '../../utils/notificationCenter';

interface ResidentialComplex {
  id: string;
  name: string;
  address: string;
  matricula?: string;
  catastral?: string;
  logoUrl?: string;
  legalTemplate?: string;
  createdAt: string;
  updatedAt: string;
}

interface ComplexFormData {
  name: string;
  address: string;
  matricula: string;
  catastral: string;
  logoUrl: string;
  legalTemplate: string;
}

const AdminComplexesPage: React.FC = () => {
  const { confirm } = useConfirm();
  const [complexes, setComplexes] = useState<ResidentialComplex[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<ComplexFormData>({
    name: '',
    address: '',
    matricula: '',
    catastral: '',
    logoUrl: '',
    legalTemplate: '',
  });

  useEffect(() => {
    fetchComplexes();
  }, []);

  const fetchComplexes = async () => {
    try {
      setLoading(true);
      const response = await api.api.get<{ success: boolean; data: ResidentialComplex[] }>('/complexes');
      const payload = (response as any).data;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setComplexes(list);
    } catch (error) {
      console.error('Error cargando conjuntos:', error);
      notifyError('Error al cargar la lista de conjuntos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      name: '',
      address: '',
      matricula: '',
      catastral: '',
      logoUrl: '',
      legalTemplate: '',
    });
  };

  const handleEdit = (complex: ResidentialComplex) => {
    setEditingId(complex.id);
    setFormData({
      name: complex.name,
      address: complex.address,
      matricula: complex.matricula || '',
      catastral: complex.catastral || '',
      logoUrl: complex.logoUrl || '',
      legalTemplate: complex.legalTemplate || '',
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({
      name: '',
      address: '',
      matricula: '',
      catastral: '',
      logoUrl: '',
      legalTemplate: '',
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim() || !formData.address.trim()) {
        notifyWarning('El nombre y la dirección son obligatorios');
        return;
      }

      if (isCreating) {
        await api.api.post('/complexes', formData);
      } else if (editingId) {
        await api.api.put(`/complexes/${editingId}`, formData);
      }

      await fetchComplexes();
      handleCancel();
    } catch (error: any) {
      console.error('Error guardando conjunto:', error);
      notifyError(error.response?.data?.message || 'Error al guardar el conjunto');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    const shouldDelete = await confirm({
      title: 'Eliminar conjunto',
      message: `¿Estás seguro de eliminar el conjunto "${name}"?`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (!shouldDelete) {
      return;
    }

    try {
      await api.api.delete(`/complexes/${id}`);
      await fetchComplexes();
    } catch (error: any) {
      console.error('Error eliminando conjunto:', error);
      notifyError(error.response?.data?.message || 'Error al eliminar el conjunto');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <LoadingState layout="page" message="Cargando conjuntos..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Conjuntos Residenciales</h1>
                <p className="text-gray-600 mt-2">
                  Administra los conjuntos residenciales del sistema. Cada conjunto puede tener su propia información legal y usuarios asociados.
                </p>
              </div>
              {!isCreating && (
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Crear Conjunto
                </button>
              )}
            </div>
          </div>

      {/* Formulario de creación/edición */}
      {(isCreating || editingId) && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {isCreating ? 'Nuevo Conjunto' : 'Editar Conjunto'}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Conjunto Residencial Los Pinos"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dirección *
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Calle 108 # 52-86, Barranquilla"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matrícula Inmobiliaria
              </label>
              <input
                type="text"
                value={formData.matricula}
                onChange={(e) => setFormData({ ...formData, matricula: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Opcional"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cédula Catastral
              </label>
              <input
                type="text"
                value={formData.catastral}
                onChange={(e) => setFormData({ ...formData, catastral: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Opcional"
              />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL del Logo
              </label>
              <input
                type="text"
                value={formData.logoUrl}
                onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://ejemplo.com/logo.png (opcional)"
              />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plantilla Legal Personalizada
              </label>
              <textarea
                value={formData.legalTemplate}
                onChange={(e) => setFormData({ ...formData, legalTemplate: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Texto legal personalizado para poderes (opcional - usa plantilla por defecto si se deja vacío)"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Guardar
            </button>
          </div>
        </div>
      )}

      {/* Lista de conjuntos */}
      <div className="space-y-3">
        {complexes.map((complex) => (
          <div key={complex.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">{complex.name}</h3>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p><strong>Dirección:</strong> {complex.address}</p>
                  {complex.matricula && (
                    <p><strong>Matrícula:</strong> {complex.matricula}</p>
                  )}
                  {complex.catastral && (
                    <p><strong>Catastral:</strong> {complex.catastral}</p>
                  )}
                  {complex.logoUrl && (
                    <p>
                      <strong>Logo:</strong>{' '}
                      <a
                        href={complex.logoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Ver logo
                      </a>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(complex)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Editar"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(complex.id, complex.name)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Eliminar"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {complexes.length === 0 && !isCreating && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            No hay conjuntos registrados
          </h3>
          <p className="text-gray-600 mb-6">
            Crea tu primer conjunto residencial para comenzar
          </p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Plus className="w-5 h-5 mr-2" />
            Crear Primer Conjunto
          </button>
        </div>
      )}
    </div>
    </div>
    </AdminLayout>
  );
};

export default AdminComplexesPage;
